import { useState } from "react";
import { FileArrowDown, FilePdf, FileCsv } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * CommissionExportCard — Vague 12.
 *
 * Compact card in the télépro portal that lets them download a clean
 * PDF+CSV commission report for any date range. Defaults to "this year".
 * The PDF is signable by an accountant; the CSV opens direct in Excel FR
 * (UTF-8 BOM + semicolons).
 */
export default function CommissionExportCard() {
  const today = new Date().toISOString().slice(0, 10);
  const year = today.slice(0, 4);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);

  const download = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("mc68_token") || "";
      const url = `${base}/api/telepro/me/commissions/export.${kind}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `commissions-${from}-au-${to}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Rapport ${kind.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="commission-export-card">
      <div>
        <div className="flex items-center gap-1.5 text-sm font-display font-medium text-primary">
          <FileArrowDown size={16} weight="bold"/> Rapport commissions
        </div>
        <div className="text-[11px] text-muted-foreground italic">
          PDF signable + CSV pour ton comptable — chiffre d'affaires, ventes et versements mensuels sur la période.
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
          Du
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="block mt-0.5 text-xs border border-border rounded-sm px-2 py-1 bg-background focus:border-primary outline-none normal-case tracking-normal font-normal tabular-nums"
            data-testid="commission-export-from"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
          Au
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="block mt-0.5 text-xs border border-border rounded-sm px-2 py-1 bg-background focus:border-primary outline-none normal-case tracking-normal font-normal tabular-nums"
            data-testid="commission-export-to"
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => download("pdf")}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] disabled:opacity-50 rounded-sm px-3 py-1.5 text-xs font-bold"
          data-testid="commission-export-pdf-btn"
        >
          <FilePdf size={13} weight="bold"/> Télécharger PDF
        </button>
        <button
          onClick={() => download("csv")}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground hover:bg-[#a86647] disabled:opacity-50 rounded-sm px-3 py-1.5 text-xs font-bold"
          data-testid="commission-export-csv-btn"
        >
          <FileCsv size={13} weight="bold"/> Télécharger CSV
        </button>
        <button
          onClick={() => { setFrom(`${year}-01-01`); setTo(today); }}
          className="text-[11px] text-muted-foreground hover:text-primary underline"
          data-testid="commission-export-reset-year"
        >
          Année en cours
        </button>
      </div>
    </div>
  );
}
